import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../../lib/LangContext";
import { homeCopy } from "../../lib/homeCopy";
import { navigateAndScroll } from "../../lib/scroll";
import { ChapterLabel, Reveal, StoryImage } from "./Story";

// Image + provenance per service. Concept images are labelled; film stills are tagged as brand film.
const MEDIA = {
  interior: { name: "craft-brush", tag: "film" },
  exterior: { name: "concept-exterior", widths: [640, 1264], tag: "concept" },
  complete: { name: "transformation-front", tag: "film" },
  sofa: { name: "concept-sofa", widths: [640, 1264], tag: "concept" },
};

const ServiceCard = ({ item, copy, index, onQuote }) => {
  const m = MEDIA[item.key];
  return (
    <Reveal delay={0.06 * index} className="group flex flex-col border border-white/[0.08] bg-[#0A0A0C] hover:border-gold/30 transition-colors duration-500">
      <StoryImage name={m.name} widths={m.widths} alt={item.title} tag={copy[m.tag]} sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw" className="aspect-[4/3]" testid={`service-${item.key}-image`} />
      <div data-testid={`service-${item.key}`} className="flex flex-col flex-1 p-7 lg:p-8">
        <span className="font-mono text-[11px] tracking-[0.3em] text-gold">{String(index + 1).padStart(2, "0")}</span>
        <h3 className="mt-4 font-heading text-white text-xl lg:text-2xl tracking-tight">{item.title}</h3>
        <p className="mt-3 text-[15px] text-zinc-400 leading-relaxed flex-1">{item.desc}</p>
        <button
          type="button"
          data-testid={`service-${item.key}-cta`}
          onClick={onQuote}
          className="mt-8 self-start inline-flex items-center gap-2 text-[12px] tracking-[0.25em] uppercase text-gold hover:text-[#E5C378] transition-colors"
        >
          {copy.cta} <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </Reveal>
  );
};

export const ServicesChapter = () => {
  const { lang } = useLang();
  const copy = homeCopy(lang);
  const c = copy.services;
  const navigate = useNavigate();
  const onQuote = () => navigateAndScroll(navigate, "contact", { flow: "price" });
  return (
    <section id="services" data-testid="services-section" data-chapter="5" className="relative bg-[#050505] py-20 md:py-28 border-t border-white/[0.06]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <Reveal><ChapterLabel index={5} label={copy.chapters[4]} testid="chapter-services-label" /></Reveal>
            <Reveal delay={0.08} as="h2" className="mt-8 font-heading text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight">{c.title}</Reveal>
          </div>
          <Reveal delay={0.16} as="p" className="lg:col-span-5 text-zinc-300 text-[15px] md:text-[17px] leading-relaxed">{c.intro}</Reveal>
        </div>
        <div className="mt-12 md:mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {c.items.map((item, i) => <ServiceCard key={item.key} item={item} copy={c} index={i} onQuote={onQuote} />)}
        </div>
      </div>
    </section>
  );
};
